/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdSetup — first-load state machine that walks the user through the
 * post-auth bootstrap pipeline (capability check → enrolment → vault
 * setup → ready). Mounted by `main.ts` after a successful challenge.
 * Emits a bubbled `ready` event on completion; emits no events for the
 * intermediate transitions (callers observe `state` directly).
 *
 * Lit light-DOM component. The `state` accessor stays manual (not a
 * `@property` decorator) because it has side effects + synchronous
 * validation: the setter fires the next state's transition method
 * and throws on invalid input. `@state` on the underlying `_state`
 * field gives Lit the reactivity hook it needs to re-render when the
 * state advances.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { authService } from '../services/auth.js';
import { t, LocaleController } from '../services/i18n.js';
import { vaultService } from '../services/vault.js';
import { TdOnboarding } from './TdOnboarding.js';
import { TdPassphrase } from './TdPassphrase.js';

const STATE_DELAY = 200;

/** Ordered phases of the post-auth bootstrap pipeline. */
export type SetupStateName =
  | 'initializing'
  | 'checkCapabilities'
  | 'retrievingUserDetails'
  | 'setupSecurityPolicies'
  | 'retrievingDisclosures'
  | 'ready';

/** Detail payload of the `ready` event TdSetup bubbles on completion. */
export interface SetupReadyDetail {
  state: SetupStateName;
}

const STATES: readonly SetupStateName[] = [
  'initializing',
  'checkCapabilities',
  'retrievingUserDetails',
  'setupSecurityPolicies',
  'retrievingDisclosures',
  'ready',
];

const isSetupState = (value: string): value is SetupStateName =>
  (STATES as readonly string[]).includes(value);

@customElement('td-setup')
export class TdSetup extends LitElement {
  @state() private _state: SetupStateName | undefined = undefined;
  @state() private _progress = 0;

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  override createRenderRoot(): HTMLElement {
    return this;
  }

  get state(): SetupStateName | undefined {
    return this._state;
  }

  set state(value: SetupStateName) {
    if (!isSetupState(value)) {
      throw new Error(
        `Invalid state: ${value}. Valid states are: ${STATES.join(', ')}`
      );
    }
    if (this._state === value) return;
    this._state = value;
    // Mirror to attribute so external `getAttribute('state')` sees the
    // current value. The recursive attributeChangedCallback call no-ops
    // via the `_state === value` guard above.
    this.setAttribute('state', value);
    this._progress = STATES.indexOf(value);
    switch (value) {
      case 'initializing':
        this.initializing();
        break;
      case 'checkCapabilities':
        this.checkCapabilities();
        break;
      case 'retrievingUserDetails':
        this.retrieveUserDetails();
        break;
      case 'setupSecurityPolicies':
        void this.setupSecurityPolicies();
        break;
      case 'retrievingDisclosures':
        this.retrievingDisclosures();
        break;
      case 'ready':
        this.completedSetup();
        break;
    }
  }

  /** Defensive copy of the canonical state list (callers may mutate). */
  get states(): SetupStateName[] {
    return [...STATES];
  }

  /** 0-based index of the current state within `STATES`. */
  get progress(): number {
    return this._progress;
  }

  static override get observedAttributes(): string[] {
    // Mirror Lit's reactive-property machinery: the `state` attribute
    // is owned by the custom getter/setter pair above, not by
    // @property. We forward attribute mutations to the setter so an
    // external `element.setAttribute('state', 'ready')` still advances
    // the machine.
    return ['state'];
  }

  override attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    // Intentionally NOT calling `super.attributeChangedCallback` —
    // Lit's reactive-element machinery expects attributes to be
    // registered via `@property`. The `state` attribute is owned by
    // the custom getter/setter above; routing through super would
    // try to look up a non-existent property descriptor.
    if (name === 'state' && oldValue !== newValue && newValue !== null) {
      if (!isSetupState(newValue)) {
        console.error(
          `Invalid state: ${newValue}. Valid states are: ${STATES.join(', ')}`
        );
        return;
      }
      this.state = newValue;
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Seed the attribute → state machine starts at the first phase.
    const initial = STATES[0];
    if (initial) {
      this.setAttribute('state', initial);
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    console.log('TdSetup disconnected');
  }

  initializing(): void {
    setTimeout(() => {
      this.state = 'checkCapabilities';
    }, STATE_DELAY);
  }

  /** Check the capabilities of the browser. Especially the crypto APIs. */
  checkCapabilities(): void {
    setTimeout(() => {
      this.state = 'retrievingUserDetails';
    }, STATE_DELAY);
  }

  retrieveUserDetails(): void {
    if (!authService.isMember()) {
      const container = this.querySelector<HTMLElement>('.setup-header');
      if (!container) return;
      const onboarding = new TdOnboarding();
      container.insertBefore(onboarding, container.firstChild);
      return;
    }
    setTimeout(() => {
      this.state = 'setupSecurityPolicies';
    }, STATE_DELAY);
  }

  async setupSecurityPolicies(): Promise<void> {
    const hasVault = await vaultService.checkVault();
    if (!hasVault) {
      const container = this.querySelector<HTMLElement>('.setup-header');
      if (!container) return;
      const passphrase = new TdPassphrase();
      container.insertBefore(passphrase, container.firstChild);
    } else {
      this.state = 'retrievingDisclosures';
    }
  }

  retrievingDisclosures(): void {
    setTimeout(() => {
      this.state = 'ready';
    }, STATE_DELAY);
  }

  completedSetup(): void {
    this.dispatchEvent(
      new CustomEvent<SetupReadyDetail>('ready', {
        detail: { state: this._state ?? 'ready' },
        bubbles: true,
        composed: true,
      })
    );
  }

  override render(): TemplateResult {
    const stateLabel = this._state ?? '';
    const progressPercent =
      STATES.length > 1 ? (this._progress / (STATES.length - 1)) * 100 : 0;
    return html`
      <div class="wa-cluster login-container">
        <div class="setup-header">
          <h2>${t('setup.heading')}</h2>
          <!-- aria-live announces each bootstrap phase to screen readers;
               the progress bar label names the otherwise anonymous
               progressbar role (#243). The state name itself is a
               technical state-machine token and stays untranslated. -->
          <div class="setup-state state-${stateLabel}" aria-live="polite">
            ${t('setup.state', { state: stateLabel })}
          </div>
          <wa-progress-bar
            label=${t('setup.progressLabel')}
            .value=${progressPercent}
          ></wa-progress-bar>
        </div>
      </div>
    `;
  }
}
